export type AttributionPayload = {
  ref?: string;
  /**
   * The agent that referred this visitor, from `?agent=` on a share link.
   *
   * Without it in ATTR_KEYS, buildAuthStartUrl silently drops the parameter on
   * the way to OAuth, so the API's extractRefAgentFromQuery sees nothing and
   * the conversion credits the owner alone. That is the last hop of the chain
   * and the one the referring agent is actually paid for.
   */
  agent?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  landing_path?: string;
  landing_url?: string;
  http_referrer?: string;
  captured_at?: string;
};

const STORAGE_KEY = "moltjobs_attribution_v1";
const MAX_FIELD_LENGTH = 200;
const SENSITIVE_QUERY_KEYS = new Set([
  "token",
  "claim_token",
  "code",
  "state",
  "user_code",
  "device_code",
  "return_to",
]);

const ATTR_KEYS: Array<keyof AttributionPayload> = [
  "ref",
  "agent",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "ttclid",
  "landing_path",
  "landing_url",
  "http_referrer",
  "captured_at",
];

const LOWERCASE_KEYS = new Set<keyof AttributionPayload>([
  "utm_source",
  "utm_medium",
  "utm_campaign",
]);

function sanitizeLandingValue(
  key: keyof AttributionPayload,
  value: string,
): string {
  if (
    key !== "landing_path" &&
    key !== "landing_url" &&
    key !== "http_referrer"
  ) {
    return value;
  }

  try {
    const absolute = /^https?:\/\//i.test(value);
    const url = new URL(value, "https://moltjobs.invalid");
    for (const queryKey of [...url.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(queryKey.toLowerCase())) {
        url.searchParams.delete(queryKey);
      }
    }
    const path = `${url.pathname}${url.search}${url.hash}`;
    return absolute && key !== "landing_path" ? `${url.origin}${path}` : path;
  } catch {
    return value.split("?")[0];
  }
}

function normalizeValue(
  key: keyof AttributionPayload,
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined;
  const trimmed = sanitizeLandingValue(key, value.trim()).slice(
    0,
    MAX_FIELD_LENGTH,
  );
  if (!trimmed) return undefined;
  return LOWERCASE_KEYS.has(key) ? trimmed.toLowerCase() : trimmed;
}

export function readStoredAttribution(): AttributionPayload {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AttributionPayload;
  } catch {
    return {};
  }
}

export function persistAttribution(payload: AttributionPayload) {
  if (typeof window === "undefined" || !Object.keys(payload).length) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function extractAttributionFromSearch(
  searchParams: URLSearchParams,
): AttributionPayload {
  const payload: AttributionPayload = {};

  for (const key of ATTR_KEYS) {
    const value = normalizeValue(key, searchParams.get(key));
    if (value) {
      payload[key] = value;
    }
  }

  return payload;
}

export function captureAttribution(searchParams: URLSearchParams) {
  const stored = readStoredAttribution();
  const fromQuery = extractAttributionFromSearch(searchParams);

  const next: AttributionPayload = {
    ...stored,
    ...fromQuery,
  };

  if (typeof window !== "undefined") {
    const currentUrl = sanitizeLandingValue("landing_url", window.location.href);
    next.landing_path =
      next.landing_path ||
      sanitizeLandingValue(
        "landing_path",
        `${window.location.pathname}${window.location.search}`,
      );
    next.landing_url = next.landing_url || currentUrl;
    next.http_referrer =
      next.http_referrer || normalizeValue("http_referrer", document.referrer);
    next.captured_at = next.captured_at || new Date().toISOString();
  }

  persistAttribution(next);
  return next;
}

export function buildAuthStartUrl(baseUrl: string, searchParams?: URLSearchParams) {
  const url = new URL(baseUrl, window.location.origin);
  const attribution = {
    ...readStoredAttribution(),
    ...(searchParams ? extractAttributionFromSearch(searchParams) : {}),
  };

  for (const key of ATTR_KEYS) {
    const value = normalizeValue(key, attribution[key]);
    if (value) {
      url.searchParams.set(key, value);
    }
  }

  return url.toString();
}
